import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Trans, t } from '@lingui/macro';
import { EyeOff, MessageSquare } from 'lucide-react';

import { RoomSimplified } from '../../../models/Room';
import { Fire } from '../../../services/FireClient';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { My } from '../../../services/My';
import { getRoomStorageStats, RoomStorageStat, formatBytes } from '../../../services/StorageQuotaService';
import { Card, CardContent } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';

const WSAdminRoomsPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [inProgress, setInProgress] = useState(false);
  const [rooms, setRooms] = useState<(RoomSimplified & Partial<RoomStorageStat>)[]>([]);

  useEffect(() => {
    if (!workspaceId) return;
    setInProgress(true);

    // Optimized: Just fetch rooms. Storage stats (storageUsage, storageCount) are now in the Room doc itself.
    Fire.ws.getWorkspaceRooms({ workspaceId }).then(res => {
      // Sort by local storage usage (desc)
      const sorted = res.rooms.sort((a, b) => (b.storageUsage || 0) - (a.storageUsage || 0));

      // Map to compatible structure or just use sorted if type aligns
      const mapped = sorted.map(r => ({
        ...r,
        usedBytes: r.storageUsage || 0,
        fileCount: r.storageCount || 0
      }));

      setRooms(mapped);
      setInProgress(false);
    });
  }, [workspaceId]);

  return (
    <div className="w-full max-w-7xl p-2 sm:p-4 md:p-6 lg:p-8 space-y-6 bg-white my-2 sm:my-6 mx-0 sm:mx-4 rounded-none sm:rounded-panel shadow-none sm:shadow-sm border-0 sm:border">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 sm:gap-4 px-2 sm:px-0">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden sm:block p-3 rounded-xl bg-slate-100 border border-slate-200">
            <MessageSquare className="h-6 w-6 text-slate-600" />
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold tracking-tight"><Trans>Workspace Rooms</Trans></h2>
            <p className="hidden sm:block text-sm text-muted-foreground"><Trans>View and manage all rooms in this workspace.</Trans></p>
          </div>
        </div>
        <span className="text-xs sm:text-sm text-muted-foreground bg-gray-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full whitespace-nowrap shrink-0"><Trans>Total {rooms.length}</Trans></span>
      </div>

      {inProgress && (
        <div className="py-8 space-y-4 px-2 sm:px-0">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />)}
        </div>
      )}

      {!inProgress && rooms.length === 0 && (
        <div className="py-12 text-center border rounded-lg border-dashed text-muted-foreground bg-gray-50 mx-2 sm:mx-0">
          <Trans>No rooms found in this workspace</Trans>
        </div>
      )}

      {/* 모바일: 구분선, 데스크탑: 카드 */}
      <div className="sm:hidden divide-y">
        {rooms.sort((a, b) => ((b as any).usedBytes ?? 0) - ((a as any).usedBytes ?? 0)).map(r => (
          <div key={r.id} className="py-3 px-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-sm truncate flex-1 min-w-0"># {r.name}</span>
              {r.visibility === 'unlisted' && (
                <EyeOff className="w-3 h-3 text-muted-foreground shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-1">
              <span>{UtilApp.dateStrToYmd(r.created, My.user.lang)}</span>
              {(r as any).usedBytes > 0 && (
                <>
                  <span>•</span>
                  <span className="text-orange-600">{formatBytes((r as any).usedBytes)}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 데스크탑 레이아웃 */}
      <div className="hidden sm:grid gap-3">
        {rooms.sort((a, b) => ((b as any).usedBytes ?? 0) - ((a as any).usedBytes ?? 0)).map(r => {
          const usedBytes = (r as any).usedBytes || 0;
          const fileCount = (r as any).fileCount || 0;
          return (
            <Card key={r.id} className="hover:bg-gray-50/50 transition-colors">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold truncate"># {r.name}</span>
                    {r.visibility === 'unlisted' && (
                      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 gap-1 shrink-0">
                        <EyeOff className="w-3 h-3" /> <Trans>Unlisted</Trans>
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span><Trans>Created {UtilApp.dateStrToDay(r.created, My.user.lang)}</Trans></span>
                    {usedBytes > 0 && (
                      <>
                        <span className="w-px h-3 bg-gray-200" />
                        <span className="font-medium text-orange-600">{formatBytes(usedBytes)}</span>
                        <span className="w-px h-3 bg-gray-200" />
                        <span>{fileCount} files</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Users Avatars */}
                <div className="flex -space-x-2 shrink-0">
                  {r.users?.slice(0, 5).map(u => (
                    <Avatar key={u.id} className="w-8 h-8 border-2 border-white bg-white">
                      <AvatarImage src={u.photoUrl ?? undefined} />
                      <AvatarFallback className="text-[10px]">{u.name.substring(0, 1)}</AvatarFallback>
                    </Avatar>
                  ))}
                  {r.users && r.users.length > 5 && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-white bg-gray-100 text-[10px] font-medium z-10 text-gray-600">
                      +{r.users.length - 5}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  );
};

export default WSAdminRoomsPage;
